import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {clearSelection} from 'src/components/data-table-selection';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {UnassignUserEntitlementsTool} from 'src/modules/bulk-update';
import {EntitlementsPagination, PreparedEntitlementsView, ResourceSuggest, useExpandRoles} from 'src/modules/entitlements';
import {ResourceNameHeader as AccountResourceNameHeader} from 'src/modules/profile/account/tabs/entitlements/assigned/components/respource-name.header';
import {useGetAccountEntitlements} from 'src/modules/profile/account/tabs/entitlements/assigned/hooks/useGetAccountEntitlements';
import {accountEntitlementsStateActions, accountEntitlementsStateSelector} from 'src/modules/profile/account/tabs/entitlements/assigned/slices/account-entitlements-state.slice';
import {accountEntitlementsActions} from 'src/modules/profile/account/tabs/entitlements/assigned/slices/account-entitlements.slice';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import StateButton from '@pb/kit-component-state-button';
import {StickyElement} from '@pb/kit-component-sticky-element';
import Typography from '@pb/kit-component-typography';

import {
    userEntitlementsAccountEntitlements,
    userEntitlementsCustomEntitlements,
    userEntitlementsCustomEntitlementsTools,
    userEntitlementsHeader,
    userEntitlementsLoadingContainer,
    userEntitlementsNoDataLabel,
} from '../user-entitlements.module.styl';

import {ResourceNameHeader as UserResourceNameHeader} from './components/respource-name.header';
import {UserEntitlementsPagination} from './components/user-entitlements-pagination';
import {
    ACCOUNT_SECTION_NAME,
    ACCOUNT_SELECTION_KEY,
    NO_ASSIGNED_ACCOUNT_ENTITLEMENT_DESCRIPTION,
    NO_ASSIGNED_USER_ENTITLEMENT_DESCRIPTION,
    USER_SECTION_NAME,
    USER_SELECTION_KEY,
} from './constants';
import {useGetUserEntitlements} from './hooks/useGetUserEntitlements';
import {userEntitlementsStateActions} from './slices/user-entitlements-state.slice';
import {userEntitlementsActions} from './slices/user-entitlements.slice';


type Props = {
    accountId: number,
    userId: number,
};

export const EntitlementsTab = ({accountId, userId}: Props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {canEditUserEntitlements} = useAdminAccessRight();

    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();

    const {
        loadingError: accountRelationsLoadingError,
        result: accountEntitlements,
        cacheKeys: accountCacheKeys,
    } = useGetAccountEntitlements({accountId});

    const {
        loadingError: userRelationsLoadingError,
        result: userEntitlements,
        cacheKeys: userCacheKeys,
    } = useGetUserEntitlements({userId});

    const {
        disabled: isAccountRolesDisabled,
        isExpanded: isAccountRolesExpanded,
        toggleRolesExpansion: toggleAccountRolesExpansion,
        expandedRoleIds: expandedRoleIdsAccount,
        setExpandedRoleIds: setExpandedRoleIdsAccount,
    } = useExpandRoles({records: accountEntitlements?.data || []});

    const {
        disabled: isUserRolesDisabled,
        isExpanded: isUserRolesExpanded,
        toggleRolesExpansion: toggleUserRolesExpansion,
        expandedRoleIds: expandedRoleIdsUser,
        setExpandedRoleIds: setExpandedRoleIdsUser,
    } = useExpandRoles({records: userEntitlements?.data || []});

    const redirectToAssignResources = useCallback(() => {
        navigate(`/profile/user/${userId}/assign-resources`);
    }, [userId, navigate]);

    useEffect(() => {
        return () => {
            dispatch(clearSelection(USER_SELECTION_KEY));
            dispatch(userEntitlementsActions.clearData());
            dispatch(userEntitlementsStateActions.clearData());
            dispatch(accountEntitlementsActions.clearData());
            dispatch(accountEntitlementsStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshResults = useCallback(() => {
        dispatch(clearSelection(USER_SELECTION_KEY));
        dispatch(userEntitlementsActions.requireRefresh());
        dispatch(accountEntitlementsActions.requireRefresh());
    }, [dispatch]);

    const filterCustomResources = useCallback((resourceIds: string[] | null, query: string) => {
        dispatch(userEntitlementsStateActions.setFilters(resourceIds ? {
            resourceIds,
            resourceQuery: query,
        } : {}));
    }, [dispatch]);

    const filterAccountResources = useCallback((resourceIds: string[] | null, query: string) => {
        dispatch(accountEntitlementsStateActions.setFilters(resourceIds ? {
            resourceIds,
            resourceQuery: query,
        } : {}));
    }, [dispatch]);

    const noUserResultMessage = useMemo(() => {
        const filteredResources = userCacheKeys.filters.resourceIds as string[] | undefined;
        if (filteredResources && !filteredResources.length) {
            return (
                <div className={userEntitlementsNoDataLabel}>
                    No resources found for the selected criteria.
                </div>
            );
        }

        if (userEntitlements && !userEntitlements.data.length) {
            return (
                <div className={userEntitlementsNoDataLabel}>
                    {NO_ASSIGNED_USER_ENTITLEMENT_DESCRIPTION}
                </div>
            );
        }

        return null;
    }, [userCacheKeys.filters, userEntitlements]);

    const noAccountResultMessage = useMemo(() => {
        const filteredResources = accountCacheKeys.filters.resourceIds as string[] | undefined;
        if (filteredResources && !filteredResources.length) {
            return (
                <div className={userEntitlementsNoDataLabel}>
                    No resources found for the selected criteria.
                </div>
            );
        }

        if (accountEntitlements && !accountEntitlements.data.length) {
            return (
                <div className={userEntitlementsNoDataLabel}>
                    {NO_ASSIGNED_ACCOUNT_ENTITLEMENT_DESCRIPTION}
                </div>
            );
        }

        return null;
    }, [accountCacheKeys.filters, accountEntitlements]);

    const canEdit = useMemo(() => canEditUserEntitlements(accountId), [canEditUserEntitlements, accountId]);

    return (
        <div className="user-entiltement-view">
            <StickyElement className={userEntitlementsCustomEntitlements}>
                <div className={`user-entitlements-header ${userEntitlementsHeader}`}>
                    <Group gap="m" justify="space-between" alignItems="center">
                        <Group gap="m" alignItems="center">
                            <div style={{width: 310}}>
                                <Typography
                                    fontWeight="semi-bold"
                                    variant="heading-level-3">
                                    <Group gap="m" alignItems="center">
                                        <Icon type={Icons.COGS}/>
                                        Custom User Entitlements
                                    </Group>
                                </Typography>
                            </div>
                            <ResourceSuggest onSearch={filterCustomResources} userId={userId} id="resource-suggest"/>
                        </Group>
                        <Group gap="m" alignItems="center">
                            <Label>Expand Roles</Label>
                            <StateButton
                                {...{
                                    name: 'expand-user-roles',
                                    disabled: isUserRolesDisabled,
                                    checked: isUserRolesExpanded,
                                    onChange: toggleUserRolesExpansion,
                                }}
                            />
                            {canEdit ? (
                                <Button variant="Type.PRIMARY" onClick={redirectToAssignResources}>
                                    Assign Resource(s)
                                </Button>
                            ) : null}
                        </Group>
                    </Group>
                </div>
            </StickyElement>
            <ServerResponseHandler serverError={userRelationsLoadingError} sectionName={USER_SECTION_NAME}>
                <BoxLoading loading={!userEntitlements}>
                    <div className={userEntitlementsLoadingContainer}>
                        {userEntitlements?.data.length ? (
                            <PreparedEntitlementsView
                                resourceFilter={(userCacheKeys.filters.resourceQuery as string | undefined) || ''}
                                dataTableId="assign-user-entitlements"
                                rows={userEntitlements.data}
                                selectionKey={USER_SELECTION_KEY}
                                customOffset={136}
                                expandedRoleIds={expandedRoleIdsUser}
                                setExpandedRoleIds={setExpandedRoleIdsUser}
                                readonly={!canEdit}
                                toolRibbonClassName={userEntitlementsCustomEntitlementsTools}
                                getColumnWidthByColumnId={getColumnWidthByColumnId}
                                onStopResize={onStopResize}
                                paginationStripe={
                                    <UserEntitlementsPagination totalCount={userEntitlements.totalCount}/>
                                }
                                shortHeader={<UserResourceNameHeader/>}>
                                {() => (
                                    <UnassignUserEntitlementsTool
                                        userId={userId}
                                        selectionKey={USER_SELECTION_KEY}
                                        onDone={refreshResults}
                                    />
                                )}
                            </PreparedEntitlementsView>
                        ) : noUserResultMessage}
                    </div>
                </BoxLoading>
            </ServerResponseHandler>
            <StickyElement className={userEntitlementsAccountEntitlements}>
                <div className={userEntitlementsHeader}>
                    <Group gap="m" justify="space-between" alignItems="center">
                        <Group gap="m" alignItems="center">
                            <div style={{width: 310}}>
                                <Typography
                                    fontWeight="semi-bold"
                                    variant="heading-level-3">
                                    <Group gap="m" alignItems="center">
                                        <Icon type={Icons.SITEMAP}/>
                                        Account Entitlements
                                    </Group>
                                </Typography>
                            </div>
                            <ResourceSuggest
                                onSearch={filterAccountResources}
                                accountId={accountId}
                                id="resource-suggest"
                            />
                        </Group>
                        <Group gap="m" alignItems="center">
                            <Label>Expand Roles</Label>
                            <StateButton
                                {...{
                                    name: 'expand-account-roles',
                                    disabled: isAccountRolesDisabled,
                                    checked: isAccountRolesExpanded,
                                    onChange: toggleAccountRolesExpansion,
                                }}
                            />
                        </Group>
                    </Group>
                </div>
            </StickyElement>
            <ServerResponseHandler serverError={accountRelationsLoadingError} sectionName={ACCOUNT_SECTION_NAME}>
                <BoxLoading loading={!accountEntitlements}>
                    <div className={userEntitlementsLoadingContainer}>
                        {accountEntitlements?.data.length ? (
                            <PreparedEntitlementsView
                                dataTableId="assign-user-account-entitlements"
                                rows={accountEntitlements.data}
                                expandedRoleIds={expandedRoleIdsAccount}
                                setExpandedRoleIds={setExpandedRoleIdsAccount}
                                selectionKey={ACCOUNT_SELECTION_KEY}
                                customOffset={186}
                                readonly={true}
                                getColumnWidthByColumnId={getColumnWidthByColumnId}
                                onStopResize={onStopResize}
                                paginationStripe={(
                                    <EntitlementsPagination
                                        totalCount={accountEntitlements.totalCount}
                                        stateActions={accountEntitlementsStateActions}
                                        stateSelector={accountEntitlementsStateSelector}
                                    />
                                )}
                                shortHeader={<AccountResourceNameHeader/>}
                                resourceFilter={(accountCacheKeys.filters.resourceQuery as string | undefined) || ''}
                            />
                        ) : noAccountResultMessage}
                    </div>
                </BoxLoading>
            </ServerResponseHandler>
        </div>
    );
};
